import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClockIcon,
  UserGroupIcon,
  FolderIcon,
  CalendarIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useTimeEntryStore } from '../store/timeEntryStore';
import { useClientStore } from '../store/clientStore';
import { useProjectStore } from '../store/projectStore';

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dailySummary, fetchDailySummary, selectedDate } = useTimeEntryStore();
  const { clients, fetchClients } = useClientStore();
  const { projects, fetchProjects } = useProjectStore();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchDailySummary(selectedDate),
        fetchClients(),
        fetchProjects(),
      ]);
      setIsLoading(false);
    };
    loadData();
  }, [selectedDate, fetchDailySummary, fetchClients, fetchProjects]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const stats = [
    {
      name: t('dashboard.todaysHours'),
      value: dailySummary ? Number(dailySummary.total_hours).toFixed(2) : '0.00',
      icon: ClockIcon,
      color: 'bg-green-500',
      lightColor: 'bg-green-100 dark:bg-green-900',
      textColor: 'text-green-600 dark:text-green-400',
    },
    {
      name: t('dashboard.totalClients'),
      value: clients.filter(c => c.is_active).length,
      icon: UserGroupIcon,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-100 dark:bg-blue-900',
      textColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      name: t('dashboard.totalProjects'),
      value: projects.filter(p => p.is_active).length,
      icon: FolderIcon,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-100 dark:bg-purple-900',
      textColor: 'text-purple-600 dark:text-purple-400',
    },
    {
      name: t('dashboard.entriesToday'),
      value: dailySummary ? dailySummary.entry_count : 0,
      icon: CalendarIcon,
      color: 'bg-orange-500',
      lightColor: 'bg-orange-100 dark:bg-orange-900',
      textColor: 'text-orange-600 dark:text-orange-400',
    },
  ];

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('dashboard.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {formatDate(selectedDate)}
          </p>
        </div>
        <button
          onClick={() => navigate('/time-entry')}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {t('dashboard.logTime')}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5"
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.lightColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.textColor}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {stat.name}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Today's Entries */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('dashboard.todaysEntries')}
          </h2>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {dailySummary && dailySummary.entries.length > 0 ? (
            dailySummary.entries.map((entry) => (
              <div
                key={entry.id}
                className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                onClick={() => navigate('/time-entry')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {entry.client_name}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">/</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {entry.project_name}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                      {entry.description}
                    </p>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {Number(entry.hours).toFixed(2)} hrs
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-12 text-center">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                {t('dashboard.noEntries')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('dashboard.noEntriesDescription')}
              </p>
              <button
                onClick={() => navigate('/time-entry')}
                className="mt-4 inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('dashboard.logFirstEntry')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          onClick={() => navigate('/clients')}
          className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:border-green-500 dark:hover:border-green-500 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900 group-hover:bg-blue-200 dark:group-hover:bg-blue-800 transition-colors">
            <UserGroupIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="ml-4 text-left">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {t('dashboard.manageClients')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('dashboard.addEditClients')}
            </p>
          </div>
        </button>

        <button
          onClick={() => navigate('/projects')}
          className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:border-green-500 dark:hover:border-green-500 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900 group-hover:bg-purple-200 dark:group-hover:bg-purple-800 transition-colors">
            <FolderIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="ml-4 text-left">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {t('dashboard.manageProjects')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('dashboard.addEditProjects')}
            </p>
          </div>
        </button>

        <button
          onClick={() => navigate('/reports')}
          className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:border-green-500 dark:hover:border-green-500 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-orange-100 dark:bg-orange-900 group-hover:bg-orange-200 dark:group-hover:bg-orange-800 transition-colors">
            <CalendarIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="ml-4 text-left">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {t('dashboard.viewReports')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('dashboard.exportData')}
            </p>
          </div>
        </button>
      </div>
    </div>
  );
};
